import { useMemo, useState } from 'react'

import CheckIcon from '@/assets/icons/icon-check.svg'
import ExpandMoreIcon from '@/assets/icons/icon-expand-more.svg'
import { IconLoading } from '@/assets/icons/icon-loading'
import { Button } from '@/components/ui/button.tsx'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command.tsx'
import { SelectOptionsType } from '@/components/ui/form/FormSelect.tsx'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.tsx'
import { Tooltip } from '@/components/ui/tooltip'
import { classNames } from '@/utils/helpers'

interface Props {
  options: SelectOptionsType
  entity: string
  placeholder?: string
  className?: string
  popoverClassName?: string
  value: string[]
  onChange: (value: string[]) => void
  loadMore?: () => void
  isLoadingMore?: boolean
  disabled?: boolean
  searchDisabled?: boolean
  disableAllSelect?: boolean
  searchPlaceholder?: string
  searchValue?: string
  onSearch?: (value: string) => Promise<void> | void
  onClose?: () => Promise<void> | void
  align?: 'start' | 'end'
}

export const MultiSelect = ({
  options,
  entity,
  placeholder,
  className,
  popoverClassName,
  value,
  onChange,
  disabled,
  searchPlaceholder,
  loadMore,
  isLoadingMore,
  searchDisabled,
  onSearch,
  searchValue,
  onClose,
  disableAllSelect,
  align = 'start',
}: Props) => {
  const [open, setOpen] = useState(false)

  const enabledOptions = options.filter((option) => !option.disabled)
  const enabledToSelectOptions = options.filter(
    (option) => !option.disabled || (option.disabled && option.forcedSelected),
  )
  const isAllSelected =
    enabledToSelectOptions.length > 0 &&
    value.length === enabledToSelectOptions.length
  const removeAll = () => onChange([])
  const selectAll = () => onChange(enabledOptions.map((event) => event.value))

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement

    if (
      loadMore &&
      target.scrollHeight - target.scrollTop === target.clientHeight
    ) {
      loadMore()
    }
  }

  const triggerContent = useMemo(() => {
    if (isAllSelected) {
      return `All ${entity}`
    }

    if (value.length > 1) {
      return `${value.length} ${entity} selected`
    }

    if (value.length === 1) {
      return options.find((event) => event.value === value[0])?.label
    }

    return placeholder
  }, [value, options, entity, placeholder, isAllSelected])

  const handleSearch = (value: string) => {
    if (onSearch) {
      onSearch(value)
    }
  }

  const handleOpen = (value: boolean) => {
    setOpen(value)

    if (onClose) {
      onClose()
    }
  }

  return (
    <Popover
      open={open}
      onOpenChange={handleOpen}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={classNames('flex justify-between', className)}
          disabled={disabled}
        >
          <div className="font-normal">{triggerContent}</div>
          <ExpandMoreIcon className="w-5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        className={classNames('z-100 w-full p-0', popoverClassName)}
      >
        <Command shouldFilter={!onSearch}>
          <CommandList>
            {!searchDisabled && (
              <CommandInput
                value={searchValue}
                placeholder={searchPlaceholder}
                onValueChange={handleSearch}
              />
            )}

            <CommandEmpty>No {entity} found.</CommandEmpty>

            {!disableAllSelect && (
              <CommandGroup>
                <CommandItem
                  onSelect={isAllSelected ? removeAll : selectAll}
                  className="cursor-pointer"
                >
                  <div
                    className={classNames(
                      'border-primary-500 mr-2 flex h-4 w-4 items-center justify-center rounded-sm border',
                      isAllSelected
                        ? 'bg-primary-500 text-white'
                        : 'opacity-50',
                    )}
                  >
                    {isAllSelected && <CheckIcon className="w-3" />}
                  </div>
                  All {entity}
                </CommandItem>
              </CommandGroup>
            )}

            <CommandSeparator />

            <CommandGroup
              onScroll={handleScroll}
              className="max-h-44 overflow-auto"
            >
              {options.map((option, index) => {
                const isSelected = value.includes(option.value)

                const itemContent = (
                  <CommandItem
                    key={option.value}
                    disabled={option.disabled || disabled}
                    className="cursor-pointer aria-disabled:pointer-events-none aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
                    onSelect={() => {
                      let newValue = [...value]

                      if (isSelected) {
                        newValue = newValue.filter(
                          (item) => item !== option.value,
                        )
                      } else {
                        newValue.push(option.value)
                      }
                      onChange(newValue)
                    }}
                  >
                    <div
                      className={classNames(
                        'border-primary-500 mr-2 flex h-4 w-4 items-center justify-center rounded-sm border',
                        isSelected ? 'bg-primary-500 text-white' : 'opacity-50',
                      )}
                    >
                      {isSelected && <CheckIcon className="w-3" />}
                    </div>
                    {option.label}
                  </CommandItem>
                )

                return option.tooltip ? (
                  <Tooltip
                    side="right"
                    content={option.tooltip}
                    key={option.value}
                    className="block cursor-default"
                  >
                    {itemContent}
                  </Tooltip>
                ) : (
                  itemContent
                )
              })}
              {isLoadingMore && (
                <IconLoading className="mx-auto my-3 animate-spin" />
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
